import { useState } from 'react'

import * as Yup from 'yup'

import {
  AddBulkEmployeesRequest,
  BulkEmployeesItem,
  EmploymentStatus,
  FamilyStatus,
  ProductIds,
  postAddBulkEmployees,
  statesApi,
} from 'core'
import { now } from 'core/application/commons/dateUtils'
import getBenefitClasses from 'core/application/csiDashboard/csiSearch/getBenefitClasses'

import { Button, Skeleton, SpreadsheetTable } from 'common/components'
import { addDays, submitDateToServerFormat, submitDateToServerFormatV2 } from 'common/dateFormatters'
import { mapEnumObjectKeys } from 'common/enumFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

export const BULK_UPLOAD_PATH = 'bulk-employee-upload'

type BulkEmployeeUploadProps = {
  companyId: string
  productId: ProductIds
  planPeriodId: string
}

const BulkEmployeeUpload = ({ companyId, productId, planPeriodId }: BulkEmployeeUploadProps) => {
  const [data, setData] = useState([])
  const { sendCommand: sendAddBulkEmployees, isLoading } = useCommandRequestV2((request: AddBulkEmployeesRequest) =>
    postAddBulkEmployees(request),
  )
  const { data: states } = useQueryRequestV2({
    queryFn: () => statesApi(),
    initialState: [],
  })
  const { data: classes } = useQueryRequestV2({
    queryFn: () => getBenefitClasses(planPeriodId),
    initialState: null,
  })

  const today = submitDateToServerFormat(addDays(now(), 1))

  const columns: (
    | {
        label: string
        key: string
        type: 'text' | 'date'
        options?: { id: string; name: string; value: string | number }[]
      }
    | { label: string; key: string; type: 'select'; options: { id: string; name: string; value: string | number }[] }
    | { label: string; key: string; type: 'date'; disableBefore?: string }
  )[] = [
    { label: 'First name', type: 'text', key: 'firstName' },
    { label: 'Last name', type: 'text', key: 'lastName' },
    { label: 'Work email', type: 'text', key: 'workEmail' },
    { label: 'Work phone', type: 'text', key: 'workPhone' },
    {
      label: 'State',
      type: 'select',
      options: states.map((state) => ({
        name: state.name,
        id: state.name,
        value: state.id,
      })),
      key: 'stateOfResidenceId',
    },
    { label: 'Zip Code', type: 'text', key: 'zipCode' },
    { label: 'Birth date', type: 'date', key: 'birthday' },
    { label: 'Hire date', type: 'date', key: 'hireDate' },
    {
      label: 'Employment Status',
      type: 'select',
      options: mapEnumObjectKeys(EmploymentStatus).map((status, index) => ({
        name: status,
        id: status,
        value: index.toString(),
      })),
      key: 'employmentStatus',
    },
    {
      label: 'Family status',
      type: 'select',
      options: mapEnumObjectKeys(FamilyStatus).map((status, index) => ({
        name: status,
        id: status,
        value: index + 1,
      })),
      key: 'familyStatusId',
    },
    {
      label: 'Class',
      type: 'select',
      options: classes?.classes.map((item) => ({
        name: item.name,
        id: item.name,
        value: item.id,
      })),
      key: 'productClassId',
    },
    { label: 'Notification date', type: 'date', key: 'notificationDate', disableBefore: today },
  ]

  const formatData = (data: any[]): BulkEmployeesItem[] => {
    return data
      .map((row) => {
        const newRow: any = {}
        columns.forEach((column, index) => {
          newRow[column.key] = row[index]?.value || null
          if (column.type === 'select') {
            newRow[column.key] = column.options.find((option) => option.name === newRow[column.key])?.value || null
          }
        })
        return newRow
      })
      .filter((obj) => Object.values(obj).some((value) => value !== null))
  }

  const userSchema = Yup.object().shape({
    firstName: Yup.string().nullable().required('First name is required'),
    lastName: Yup.string().nullable().required('Last name is required'),
    workEmail: Yup.string().email('Must be a valid email address').nullable().required('Work email is required'),
    stateOfResidenceId: Yup.string().nullable().required('State is required'),
    zipCode: Yup.string()
      .length(5, 'Zip code should be exactly 5 characters long')
      .nullable()
      .required('Zip code is required'),
    birthday: Yup.date()
      .nullable()
      .when('productId', {
        is: ProductIds.IchraId,
        then: Yup.date().typeError('Must be a valid date').nullable().required('Birth date is required'),
        else: Yup.date().typeError('Must be a valid date'),
      }),
    hireDate: Yup.date().nullable().required('Hire date is required'),
    employmentStatus: Yup.string().nullable().required('Employment status is required'),
    familyStatusId: Yup.string().nullable().required('Family status is required'),
    notificationDate: Yup.date().typeError('Must be a valid date').nullable(),
    productClassId: Yup.string().nullable().required('Class is required'),
  })

  const usersArraySchema = Yup.array().of(userSchema)

  function extractIndex(path) {
    const match = path.match(/^\[(\d+)\]/)
    return match ? Number(match[1]) : null
  }

  const validateData = (data) => {
    try {
      usersArraySchema.validateSync(data?.map((d) => ({ ...d, productId })) || [], { abortEarly: false })
      return { valid: true, errorMessages: null }
    } catch (error) {
      const indexedErrors = error?.inner.map((err) => {
        return { message: err.message, index: extractIndex(err.path) }
      })
      return { valid: false, errorMessages: indexedErrors }
    }
  }

  const validations = validateData(formatData(data))

  const rearrangeValidations = (arr) => {
    let output = []

    for (const item of arr) {
      if (!output[item.index]) {
        output[item.index] = []
      }
      output[item.index].push(item.message)
    }

    return output
  }

  const handleSubmit = async () => {
    const currentData = formatData(data)
    const formatDate = (date: string) => (date ? submitDateToServerFormat(date) : null)

    const res = await sendAddBulkEmployees({
      companyId,
      productId,
      items: currentData.map((item) => ({
        ...item,
        familyStatusId: Number(item.familyStatusId),
        employmentStatus: Number(item.employmentStatus),
        birthday: formatDate(item.birthday),
        hireDate: formatDate(item.hireDate),
        notificationDate: formatDate(item.notificationDate),
      })),
    })

    if (res.completed) {
      window.location.reload()
    }
  }

  return (
    <Skeleton active loading={!states}>
      <div className="mb-16 font-16 text-light-bold">Bulk Employee Upload</div>
      <div className="mb-8">
        <SpreadsheetTable
          defaultData={data}
          onChange={(nextData) => {
            setData(nextData)
          }}
          columns={columns}
        />
      </div>
      <Button
        key="submit"
        type="primary"
        loading={isLoading}
        disabled={!validations.valid || formatData(data).length === 0}
        onClick={handleSubmit}
      >
        Submit
      </Button>
      {validations.errorMessages &&
        rearrangeValidations(validations.errorMessages || []).map((error, index) => (
          <div className="color-red-6 mt-8">
            <div className="flex">
              Row {index + 1}: {error.join(', ')}
            </div>
          </div>
        ))}
    </Skeleton>
  )
}

export default BulkEmployeeUpload
